/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable jsx-a11y/anchor-is-valid */
/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import {
  IconBuilding,
  IconBuildingCastle,
  IconChecks,
  IconCloudDown,
  IconContract,
  IconCopy,
  IconDetails,
  IconEdit,
  IconFileText,
  IconId,
  IconLocation,
  IconMail,
  IconMapPin,
  IconPhone,
  IconUserCheck,
  IconX,
} from "@tabler/icons-react";

import NewCommonModal from "../../../components/common/NewCommonModal";
import { useForm } from "react-hook-form";
import formatDate from "../../../utilities/helper";
import toast from "react-hot-toast";
import { usePermission } from "../../../hooks/usePermissionTabs";
import NoDataFound from "../../../components/common/NoDataFound";
import NormalSearch from "../../../components/common/NormalSearch";
import {
  useFetchOutSourceDriversData,
  useFetchOutSourcesDataDetails,
  useUpdateOutSourceData,
} from "../../../apis/outSourceApis";
import AddOutSourceModal from "./AddOutSourceModal";
import { T, useLanguage } from "../../../context/LanguageContext";

type Props = {
  id: string;
  open: boolean;
  onClose: () => void;
};

interface DocumentLinkProps {
  value?: string;
  docUrl?: string;
  label: string;
}

const titleColors: Record<string, string> = {
  Location: "text-blue-600",
  "Other Details": "text-green-600",
  Documents: "text-orange-600",
  "Primary Contact": "text-purple-600",
};

const InfoBlock = ({
  title,
  children,
  className,
}: {
  title: string;
  children: React.ReactNode;
  className?: string;
}) => (
  <div>
    <h4
      className={`text-sm font-semibold text-[#212121] mb-2 uppercase tracking-wide ${
        titleColors[title] || "text-gray-600"
      } `}
    >
      <T>{title}</T>
    </h4>
    <div className={` ${className || "space-y-2"}`}>{children}</div>
  </div>
);

const Row = ({
  icon,
  label,
  value,
  icon2,
  className,
}: {
  icon?: React.ReactNode;
  icon2?: React.ReactNode;
  label: string;
  className?: string;
  value: React.ReactNode;
}) => (
  <div className={`flex items-center justify-between gap-2 ${className}`}>
    <div className="flex items-center gap-2">
      {icon && <div className="mt-0.5 text-gray-500">{icon}</div>}
      <span className="font-medium text-sm text-[#212121]"><T>{label}</T>: </span>
    </div>
    <div className="flex items-center gap-2">
      <span className="break-all text-xs">{value}</span>
      {icon2 && <div className="mt-0.5 text-gray-500">{icon2}</div>}
    </div>
  </div>
);

const TabButton = ({
  active,
  onClick,
  icon,
  label,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
}) => (
  <button
    onClick={onClick}
    className={`px-4 py-2 text-sm font-medium rounded-lg transition-all flex items-center gap-2 ${
      active
        ? "bg-[#FBD037]/20 text-[#000000] shadow-sm border"
        : "bg-slate-100 text-slate-600 border"
    }`}
  >
    {icon}
    <T>{label}</T>
  </button>
);

const DocumentLink = ({ value, docUrl, label }: DocumentLinkProps) => {
  if (!value && !docUrl) return null;

  return (
    <div className="flex items-center gap-2">
      {value && <span className="font-medium text-gray-800">{value}</span>}
      {docUrl && (
        <a
          href={docUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center justify-center hover:bg-[#FBD037]/20 p-0.5 px-1 rounded"
          title={`View ${label} Document`}
          aria-label={`View ${label} Document`}
        >
          <IconCloudDown strokeWidth={1.5} size={22} />
        </a>
      )}
    </div>
  );
};

type FormValues = {
  companyName: string;
  mobileNo: string;
  email: string;
  address: string;
  vatNumber: string;
  vatDoc: File;
  crNumber: string;
  crDoc: File;
  serviceAreaCoverage: string;
  contractStartDate: Date;
  contractEndDate?: Date;
  billingCycle: string;
  contractDoc: File;
  primaryContactName: string;
  primaryContactNumber: string;
  primaryEmailAddress: string;
  warehouseId: string;
  cityId?: string;
  specialization?: string;
  companyLogo: File;
  contactPersonName: string;
};

const SectionHeader = ({ icon: Icon, title }: { icon: any; title: string }) => (
  <div className="flex items-center gap-2 border-b border-[#FBD037]/30 pb-2 mb-3">
    <div className="p-1.5 rounded-lg bg-[#FBD037]/20">
      <Icon size={16} className="text-[#212121]" stroke={1.5} />
    </div>
    <p className="text-[#212121]/80 uppercase text-[11px] font-bold tracking-wide">
      <T>{title}</T>
    </p>
  </div>
);

export default function ViewOutSourceModal({ id, open, onClose }: Props) {
  const { t } = useLanguage();
  const { data, refetch, isFetching } = useFetchOutSourcesDataDetails(id);
  const [copied, setCopied] = useState(false);
  const { mutate, isPending } = useUpdateOutSourceData(id);
  const [pageNo, setPageNo] = useState(1);
  const [limit] = useState(10000);
  const [searchFor, setSearchFor] = useState("");
  const { data: driversData, refetch: refetchDrivers } =
    useFetchOutSourceDriversData(id, pageNo, limit, searchFor);
  const [permissionModal, setPermissionModal] = useState(false);
  const [editDialog, setEditDialog] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<any>({
    vatDoc: false,
    crDoc: false,
    contractDoc: false,
    companyLogo: false,
  });
  const credentialToggleModal = () => {
    setEditDialog(!editDialog);
    if (!editDialog) return;
    reset();
    clearErrors();
    setSelectedDocument({
      vatDoc: false,
      crDoc: false,
      contractDoc: false,
      companyLogo: false,
    });
  };
  const {
    register,
    handleSubmit,
    clearErrors,
    reset,
    setValue,
    watch,
    control,
    formState: { errors },
  } = useForm<FormValues>();

  useEffect(() => {
    if (id) {
      refetch();
    }
  }, [id]);

  const { allowed: canUpdateOutSource, isLoading } = usePermission(
    "out_source",
    "update",
  );

  const [activeTab, setActiveTab] = useState<
    "company" | "locations" | "details"
  >("company");

  const handleUpdateOutSource = (data: any) => {
    let { contractDoc, crDoc, vatDoc, companyLogo, ...otherData } = data;
    mutate(
      {
        ...otherData,
        ...(selectedDocument?.contractDoc && {
          contractDoc: data?.contractDoc,
        }),
        ...(selectedDocument?.vatDoc && { vatDoc: data?.vatDoc }),
        ...(selectedDocument?.crDoc && { crDoc: data?.crDoc }),
        ...(selectedDocument?.companyLogo && {
          companyLogo: data?.companyLogo,
        }),
        ...(watch("companyLogo") && { companyLogoAvailabel: true }),
        ...(watch("vatDoc") && { vatDocAvailabel: true }),
        ...(watch("crDoc") && { crDocAvailabel: true }),
        ...(watch("contractDoc") && { contractDocAvailabel: true }),
      },
      {
        onSuccess: () => {
          credentialToggleModal();
          reset();
          refetch();
          toast.success(t("Outsource Update Successfully."));
        },
        onError: (error) => {
          console.log(error, "error");
          const errorMessage =
            error?.response?.data?.error?.message ||
            error?.response?.data?.message ||
            error?.message ||
            (typeof error === "string" ? error : null) ||
            "Failed to Update OutSource.";

          toast.error(t(errorMessage));
        },
      },
    );
  };

  const handleCopy = async (text?: string) => {
    if (!text) return;

    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);

      setTimeout(() => {
        setCopied(false);
      }, 1500);
    } catch (err) {
      console.error("Copy failed", err);
    }
  };

  const smartEmail = (email?: string) => {
    if (!email) return "";

    const [name, domain] = email.split("@");
    if (!domain) return email;

    const [domainName, extension] = domain.split(".");

    const shortName = name.length > 5 ? `${name.slice(0, 4)}...` : name;

    const shortDomain =
      domainName && domainName.length > 4
        ? `${domainName.slice(0, 3)}...`
        : domainName;

    return `${shortName}@${shortDomain}.${extension || ""}`;
  };

  if (!open) return null;

  const logoSrc =
    data?.companyLogo &&
    data.companyLogo !== "null" &&
    data.companyLogo !== "undefined" &&
    data.companyLogo !== ""
      ? data.companyLogo
      : "/img/dummy-blank-profile.png";

  return (
    <div className="relative bg-white rounded-3xl shadow-sm border flex flex-col overflow-hidden">
      {/* Modal Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-white">
        <div className="flex items-center gap-2">
          <div className="w-14 h-14 rounded-xl overflow-hidden">
            <img
              src={logoSrc}
              onError={(e) => {
                (e.target as HTMLImageElement).src =
                  "/img/dummy-blank-profile.png";
              }}
              alt="Out Source Profile"
              className="w-full h-full object-cover rounded-xl"
            />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-[#212121]">
              {data?.companyName || t("Out Source Details")}
            </h2>
            <p className="text-sm text-[#212121]/70">
              {formatDate(data?.contractStartDate)}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {!editDialog && canUpdateOutSource && (
            <button
              onClick={() => setEditDialog(true)}
              className="p-2 rounded-lg border border-[#D2D2D2] hover:bg-gray-100 transition-colors"
              title={t("Edit Out Source")}
            >
              <IconEdit size={18} />
            </button>
          )}
          {editDialog && (
            <button
              onClick={handleSubmit(handleUpdateOutSource)}
              disabled={isPending}
              className="px-4 py-2 bg-[#FBD037] text-[#212121] rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isPending ? t("Updating...") : t("Update")}
            </button>
          )}
          <button
            onClick={() => {
              setEditDialog(false);
              onClose();
            }}
            className="p-2 rounded-lg border border-[#D2D2D2] hover:bg-gray-100 transition-colors"
          >
            <IconX size={20} />
          </button>
        </div>
      </div>

      {/* Modal Body */}
      <div className="flex-1 overflow-y-auto hide-scrollbar">
        {isFetching ? (
          <div className="w-full space-y-4 p-10 text-center">
            <div className="animate-pulse">{t("Loading...")}</div>
          </div>
        ) : editDialog ? (
          <div className="py-2">
            <AddOutSourceModal
              register={register}
              errors={errors}
              setValue={setValue}
              watch={watch}
              clearErrors={clearErrors}
              control={control}
              setSelectedDocument={setSelectedDocument}
              outSourceData={data}
            />
          </div>
        ) : (
          <div className="relative p-6 bg-white">
            {/* Tabs */}
            <div className="flex gap-2 mb-6 border-b border-gray-200/50 pb-3">
              <TabButton
                active={activeTab === "company"}
                onClick={() => setActiveTab("company")}
                icon={<IconBuilding size={14} />}
                label="Company Info"
              />
              <TabButton
                active={activeTab === "locations"}
                onClick={() => setActiveTab("locations")}
                icon={<IconMapPin size={14} />}
                label="Locations"
              />
              <TabButton
                active={activeTab === "details"}
                onClick={() => setActiveTab("details")}
                icon={<IconUserCheck size={14} />}
                label="Primary/Other"
              />
            </div>

            {/* Tab content conditional rendering */}
            {activeTab === "company" && (
              <div className="space-y-6">
                {/* Company Info Card */}
                <div className="group  rounded-2xl bg-gradient-to-b from-white/90 to-white/60 backdrop-blur-xl border border-[#D2D2D2]/70 p-4 transition-all duration-300 space-y-3">
                  <div className="absolute inset-0 rounded-3xl bg-gradient-to-tr from-yellow-200/20 to-transparent pointer-events-none" />
                  <SectionHeader icon={IconBuilding} title="Company Info" />
                  <div className="space-y-2">
                    <Row
                      icon={<IconContract size={18} />}
                      label="Contact Person"
                      value={data?.contactPersonName || "-"}
                    />
                    <Row
                      icon={<IconPhone size={18} />}
                      label="Mobile"
                      value={data?.mobileNo || "-"}
                    />
                    <Row
                      icon={<IconMail size={18} />}
                      label="Email"
                      value={data?.email || "-"}
                    />
                    <Row
                      icon={<IconContract size={18} />}
                      label="Joined"
                      value={formatDate(data?.contractStartDate)}
                    />

                    <Row
                      icon={<IconUserCheck size={18} />}
                      label="Drivers"
                      value={driversData?.total || 0}
                    />

                    {/* <Row
                      icon={<IconUserCheck size={18} />}
                      label="Status"
                      value={
                        <span
                          className={`px-2 py-0.5 text-xs rounded-full font-medium ${
                            data?.status
                              ? "bg-green-100 text-green-700"
                              : "bg-red-100 text-red-600"
                          }`}
                        >
                          {data?.status ? "ACTIVE" : "INACTIVE"}
                        </span>
                      }
                    /> */}
                    {/* <div
                      className="flex items-center justify-between gap-2 cursor-pointer hover:bg-gray-50 p-1.5 rounded-lg transition-colors border-t pt-2 border-gray-100/50"
                      onClick={() => setPermissionModal(true)}
                    >
                      <div className="flex items-center gap-2">
                        <IconUserCheck size={18} className="text-gray-500" />
                        <span className="font-medium text-sm text-[#212121]">Drivers:</span>
                      </div>
                      <span className="text-sm text-yellow-800 font-semibold underline">
                        {driversData?.total || 0}
                      </span>
                    </div> */}
                  </div>
                </div>

                {/* Documents Card */}
                {/* Documents Card */}
                <div className="group  rounded-2xl bg-gradient-to-b from-white/90 to-white/60 backdrop-blur-xl border border-[#D2D2D2]/70 p-4 transition-all duration-300 space-y-3">
                  <div className="absolute inset-0 rounded-3xl bg-gradient-to-tr from-yellow-200/20 to-transparent pointer-events-none" />
                  <SectionHeader icon={IconFileText} title="Documents" />

                  <div className="space-y-2">
                    <Row
                      icon={<IconContract size={18} />}
                      label="Contract Date"
                      value={
                        <DocumentLink
                          value={formatDate(data?.contractStartDate)}
                          docUrl={data?.contractDoc}
                          label="Contract Date"
                        />
                      }
                    />

                    <Row
                      icon={<IconFileText size={18} />}
                      label="Tax/VAT"
                      value={
                        <DocumentLink
                          value={data?.vatNumber}
                          docUrl={data?.vatDoc}
                          label="VAT"
                        />
                      }
                    />

                    <Row
                      icon={<IconId size={18} />}
                      label="CR Number"
                      value={
                        <DocumentLink
                          value={data?.crNumber}
                          docUrl={data?.crDoc}
                          label="CR Doc"
                        />
                      }
                    />
                  </div>
                </div>
              </div>
            )}

            {activeTab === "locations" && (
              <div className="group  rounded-2xl bg-gradient-to-b from-white/90 to-white/60 backdrop-blur-xl border border-[#D2D2D2]/70 p-4 transition-all duration-300 space-y-3">
                <div className="absolute inset-0 rounded-3xl bg-gradient-to-tr from-yellow-200/20 to-transparent pointer-events-none" />
                <SectionHeader icon={IconLocation} title="Location" />
                <div className="space-y-2">
                  <Row
                    icon={<IconBuilding size={18} />}
                    label="Warehouse"
                    value={data?.warehouseName || "-"}
                  />
                  <Row
                    icon={<IconBuildingCastle size={18} />}
                    label="City"
                    value={data?.cityName || "-"}
                  />
                  <Row
                    icon={<IconMapPin size={18} />}
                    label="Address"
                    value={data?.address || "-"}
                  />
                </div>
              </div>
            )}

            {activeTab === "details" && (
              <div className="space-y-6">
                {/* Primary Contact Card */}
                <div className="group  rounded-2xl bg-gradient-to-b from-white/90 to-white/60 backdrop-blur-xl border border-[#D2D2D2]/70 p-4 transition-all duration-300 space-y-3">
                  <div className="absolute inset-0 rounded-3xl bg-gradient-to-tr from-yellow-200/20 to-transparent pointer-events-none" />
                  <SectionHeader icon={IconContract} title="Primary Contact" />
                  <div className="space-y-2">
                    <Row
                      icon={<IconUserCheck size={18} />}
                      label="Name"
                      value={data?.primaryContactName || "-"}
                    />
                    <Row
                      icon={<IconPhone size={18} />}
                      label="Number"
                      value={data?.primaryContactNumber || "-"}
                    />
                    <Row
                      icon={<IconMail size={18} />}
                      label="Email"
                      value={data?.primaryEmailAddress || "-"}
                    />
                  </div>
                </div>

                {/* Other Details Card */}
                <div className="group  rounded-2xl bg-gradient-to-b from-white/90 to-white/60 backdrop-blur-xl border border-[#D2D2D2]/70 p-4 transition-all duration-300 space-y-3">
                  <div className="absolute inset-0 rounded-3xl bg-gradient-to-tr from-yellow-200/20 to-transparent pointer-events-none" />
                  <SectionHeader icon={IconDetails} title="Other Details" />
                  <div className="space-y-2">
                    <Row
                      icon={<IconContract size={18} />}
                      label="Billing"
                      value={data?.billingCycle?.toLowerCase() || "-"}
                      className="capitalize"
                    />
                    <Row
                      icon={<IconMapPin size={18} />}
                      label="Coverage"
                      value={data?.serviceAreaCoverage || "-"}
                    />
                    <Row
                      icon={<IconBuilding size={18} />}
                      label="Specializations"
                      value={data?.specialization || "-"}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {permissionModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="animate-fadeIn bg-[#F7F6F2] rounded-3xl w-[500px] max-h-[76vh] flex flex-col overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <h2 className="text-lg font-medium text-[#212121]">
                <T>Driver</T> ({driversData?.total || 0})
              </h2>
              <button
                onClick={() => setPermissionModal(false)}
                className="p-2 rounded-full border border-[#D2D2D2]"
              >
                <IconX size={20} />
              </button>
            </div>

            <div className="px-4 py-3">
              <NormalSearch
                value={searchFor}
                onChange={(e) => setSearchFor(e.target.value)}
              />
            </div>

            <div className="flex-1 overflow-y-auto px-4 pb-4 space-y-2">
              {driversData?.drivers?.length > 0 ? (
                driversData.drivers.map((driver: any, index: number) => (
                  <div
                    key={driver.id}
                    className="flex items-center gap-3 py-2 border-b last:border-none"
                  >
                    <span className="h-8 w-8 flex justify-center items-center text-[#212121]/70 rounded-full border">
                      {(pageNo - 1) * limit + index + 1}
                    </span>

                    <div className="flex flex-col">
                      <p className="text-sm font-medium">{driver.name}</p>
                      <p className="text-xs text-gray-500">
                        {driver.mobileNo} • {driver.email}
                      </p>
                    </div>
                  </div>
                ))
              ) : (
                <NoDataFound title={t("No Drivers Found")} message="" />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
